import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Send,
  RefreshCw,
  User,
  Building,
  Phone,
  Mail,
  Save
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const MerchantDocumentsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [profileSaved, setProfileSaved] = useState(false);
  
  // Merchant Profile Form State
  const [merchantProfile, setMerchantProfile] = useState({
    contact_name: "",
    company_name: "",
    phone_number: "",
    email: "",
    business_brief: "",
    business_address: "",
    business_type: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get required documents
      const reqRes = await fetch(`${API}/merchant/required-documents`);
      if (reqRes.ok) {
        const data = await reqRes.json();
        setRequiredDocuments(data.documents || []);
      }

      // Get verification status
      const statusRes = await fetch(`${API}/merchant/verification-status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (statusRes.ok) {
        const data = await statusRes.json();
        setVerificationStatus(data);
        setUploadedDocuments(data.documents || []);
      }
      
      // Get merchant profile if exists
      const profileRes = await fetch(`${API}/merchant/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (profileRes.ok) {
        const data = await profileRes.json();
        if (data.profile) {
          setMerchantProfile({
            contact_name: data.profile.contact_name || "",
            company_name: data.profile.company_name || "",
            phone_number: data.profile.phone_number || "",
            email: data.profile.email || user?.email || "",
            business_brief: data.profile.business_brief || "",
            business_address: data.profile.business_address || "",
            business_type: data.profile.business_type || ""
          });
          setProfileSaved(true);
        } else {
          // Pre-fill email from user
          setMerchantProfile(prev => ({
            ...prev,
            email: user?.email || "",
            contact_name: user?.name || ""
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    // Validate required fields
    if (!merchantProfile.contact_name || !merchantProfile.company_name || !merchantProfile.phone_number || !merchantProfile.email) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSavingProfile(true);
    try {
      const response = await fetch(`${API}/merchant/profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify(merchantProfile)
      });
      
      if (response.ok) {
        toast.success("Business details saved successfully!");
        setProfileSaved(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save profile");
      }
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    setUploadingType(documentType);

    try {
      // Use FormData for actual file upload (stored as base64 on server)
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", documentType);
      
      const response = await fetch(`${API}/merchant/documents/upload-file`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: formData
      });

      // Clone response to handle body stream issues
      let text;
      try {
        text = await response.clone().text();
      } catch {
        text = await response.text();
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        toast.error("Invalid response from server");
        return;
      }

      if (response.ok) {
        toast.success(`${getDocumentLabel(documentType)} uploaded successfully`);
        fetchData();
      } else {
        toast.error(data.detail || "Failed to upload document");
      }
    } catch (error) {
      console.error("Document upload error:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingType(null);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/merchant/documents/submit-for-review`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Documents submitted for review!");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit documents");
      }
    } catch (error) {
      toast.error("Failed to submit documents");
    } finally {
      setSubmitting(false);
    }
  };

  const getDocumentLabel = (type) => {
    const doc = requiredDocuments.find(d => d.type === type);
    return doc?.label || type;
  };

  const getDocumentStatus = (type) => {
    const doc = uploadedDocuments.find(d => d.document_type === type || d.type === type);
    return doc?.status || "not_uploaded";
  };

  const getUploadedDocument = (type) => {
    return uploadedDocuments.find(d => d.document_type === type || d.type === type);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "pending":
        return <Clock className="h-5 w-5 text-amber-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700",
      pending: "bg-amber-100 text-amber-700",
      not_uploaded: "bg-slate-100 text-slate-600"
    };
    const labels = {
      approved: "Approved",
      rejected: "Rejected",
      pending: "Pending Review",
      not_uploaded: "Not Uploaded"
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status] || styles.not_uploaded}`}>
        {labels[status] || "Not Uploaded"}
      </span>
    );
  };

  // Only required documents must be uploaded
  const allRequiredDocumentsUploaded = requiredDocuments
    .filter(doc => doc.required !== false)
    .every(doc => uploadedDocuments.some(d => d.document_type === doc.type));

  const canSubmit = allRequiredDocumentsUploaded && verificationStatus?.merchant_status === "pending_documents";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/merchant" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <img src={COMPANY_LOGO} alt="V" className="h-8 w-auto brightness-0" />
              <div className="h-6 w-px bg-slate-300" />
              <span className="font-brand text-sm font-bold text-slate-900">VEYRONIX</span>
            </div>
            <span className="text-sm text-slate-500">Document Verification</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Status Banner */}
        {verificationStatus && (
          <div className={`rounded-xl p-6 mb-8 ${
            verificationStatus.merchant_status === "approved" ? "bg-emerald-50 border border-emerald-200" :
            verificationStatus.merchant_status === "rejected" ? "bg-red-50 border border-red-200" :
            verificationStatus.merchant_status === "pending_approval" ? "bg-amber-50 border border-amber-200" :
            "bg-indigo-50 border border-indigo-200"
          }`}>
            <div className="flex items-start gap-4">
              {verificationStatus.merchant_status === "approved" ? (
                <CheckCircle className="h-8 w-8 text-emerald-500 flex-shrink-0" />
              ) : verificationStatus.merchant_status === "rejected" ? (
                <XCircle className="h-8 w-8 text-red-500 flex-shrink-0" />
              ) : verificationStatus.merchant_status === "pending_approval" ? (
                <Clock className="h-8 w-8 text-amber-500 flex-shrink-0" />
              ) : (
                <Upload className="h-8 w-8 text-indigo-500 flex-shrink-0" />
              )}
              <div>
                <h2 className="font-heading font-semibold text-lg text-slate-900">
                  {verificationStatus.merchant_status === "approved" ? "Business Verified!" :
                   verificationStatus.merchant_status === "rejected" ? "Application Rejected" :
                   verificationStatus.merchant_status === "pending_approval" ? "Under Review" :
                   "Upload Required Documents"}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {verificationStatus.merchant_status === "approved" ? 
                    "Your business is verified and you can now sell on VEYRONIX." :
                   verificationStatus.merchant_status === "rejected" ? 
                    verificationStatus.rejection_reason || "Please contact support for more information." :
                   verificationStatus.merchant_status === "pending_approval" ? 
                    "Your documents are being reviewed. This usually takes 1-2 business days." :
                   `Upload ${requiredDocuments.length} documents to verify your business.`}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  {verificationStatus.documents_uploaded}/{verificationStatus.documents_required} documents uploaded
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Business Details Form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Building className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-900">Business Details</h3>
                <p className="text-sm text-slate-500">Please fill in your business information</p>
              </div>
            </div>
            {profileSaved && (
              <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Saved
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_name" className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-400" />
                Contact Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="contact_name"
                placeholder="Your full name"
                value={merchantProfile.contact_name}
                onChange={(e) => setMerchantProfile({...merchantProfile, contact_name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company_name" className="flex items-center gap-2">
                <Building className="h-4 w-4 text-slate-400" />
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="company_name"
                placeholder="Your business/company name"
                value={merchantProfile.company_name}
                onChange={(e) => setMerchantProfile({...merchantProfile, company_name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone_number" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-slate-400" />
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone_number"
                placeholder="+44 7XXX XXXXXX"
                value={merchantProfile.phone_number}
                onChange={(e) => setMerchantProfile({...merchantProfile, phone_number: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400" />
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="business@example.com"
                value={merchantProfile.email}
                onChange={(e) => setMerchantProfile({...merchantProfile, email: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="business_type">Business Type</Label>
              <Input
                id="business_type"
                placeholder="e.g., Retail, Restaurant, Services"
                value={merchantProfile.business_type}
                onChange={(e) => setMerchantProfile({...merchantProfile, business_type: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="business_address">Business Address</Label>
              <Input
                id="business_address"
                placeholder="Street address, City, Postcode"
                value={merchantProfile.business_address}
                onChange={(e) => setMerchantProfile({...merchantProfile, business_address: e.target.value})}
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="business_brief">Business Brief</Label>
              <Textarea
                id="business_brief"
                placeholder="Tell us about your business - what products/services do you offer, your target market, etc."
                rows={3}
                value={merchantProfile.business_brief}
                onChange={(e) => setMerchantProfile({...merchantProfile, business_brief: e.target.value})}
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="bg-indigo-600 hover:bg-indigo-700 rounded-full"
            >
              {savingProfile ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Details
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Documents List */}
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-slate-900">Required Documents</h3>
          
          {requiredDocuments.filter(doc => doc.required !== false).map((doc, idx) => {
            const status = getDocumentStatus(doc.type);
            const uploaded = getUploadedDocument(doc.type);
            
            return (
              <div 
                key={doc.type} 
                className="bg-white rounded-xl border border-slate-200 p-6"
                data-testid={`document-row-${idx}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      status === "approved" ? "bg-emerald-100" :
                      status === "rejected" ? "bg-red-100" :
                      status === "pending" ? "bg-amber-100" :
                      "bg-slate-100"
                    }`}>
                      <FileText className={`h-5 w-5 ${
                        status === "approved" ? "text-emerald-600" :
                        status === "rejected" ? "text-red-600" :
                        status === "pending" ? "text-amber-600" :
                        "text-slate-500"
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">{doc.label}</span>
                        {getStatusBadge(status)}
                      </div>
                      {uploaded && (
                        <p className="text-sm text-slate-500">
                          {uploaded.file_name}
                          {uploaded.review_notes && status === "rejected" && (
                            <span className="text-red-600 block mt-1">
                              Note: {uploaded.review_notes}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {status !== "approved" && verificationStatus?.merchant_status !== "pending_approval" && (
                      <div className="relative">
                        <input
                          type="file"
                          id={`file-${doc.type}`}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={(e) => handleFileUpload(doc.type, e.target.files?.[0])}
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          disabled={uploadingType === doc.type}
                        />
                        <Button 
                          variant={uploaded ? "outline" : "default"}
                          size="sm"
                          className="rounded-full pointer-events-none"
                          disabled={uploadingType === doc.type}
                        >
                          {uploadingType === doc.type ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          {uploaded ? "Re-upload" : "Upload"}
                        </Button>
                      </div>
                    )}
                    {getStatusIcon(status)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Optional Documents Section */}
        {requiredDocuments.filter(doc => doc.required === false).length > 0 && (
          <div className="space-y-4 mt-8">
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              Optional Documents
              <span className="text-xs font-normal px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                Not required for approval
              </span>
            </h3>
            
            {requiredDocuments.filter(doc => doc.required === false).map((doc, idx) => {
              const status = getDocumentStatus(doc.type);
              const uploaded = getUploadedDocument(doc.type);
              
              return (
                <div 
                  key={doc.type} 
                  className="bg-white rounded-xl border border-dashed border-slate-300 p-6"
                  data-testid={`optional-document-row-${idx}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        status === "approved" ? "bg-emerald-100" :
                        status === "rejected" ? "bg-red-100" :
                        status === "pending" ? "bg-amber-100" :
                        "bg-slate-50"
                      }`}>
                        <FileText className={`h-5 w-5 ${
                          status === "approved" ? "text-emerald-600" :
                          status === "rejected" ? "text-red-600" :
                          status === "pending" ? "text-amber-600" :
                          "text-slate-400"
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-700">{doc.label}</span>
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">Optional</span>
                          {status !== "not_uploaded" && getStatusBadge(status)}
                        </div>
                        {uploaded ? (
                          <p className="text-sm text-slate-500">
                            {uploaded.file_name}
                            {uploaded.review_notes && status === "rejected" && (
                              <span className="text-red-600 block mt-1">
                                Note: {uploaded.review_notes}
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-400">Upload if applicable to your business</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {status !== "approved" && verificationStatus?.merchant_status !== "pending_approval" && (
                        <div className="relative">
                          <input
                            type="file"
                            id={`file-optional-${doc.type}`}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={(e) => handleFileUpload(doc.type, e.target.files?.[0])}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            disabled={uploadingType === doc.type}
                          />
                          <Button 
                            variant="outline"
                            size="sm"
                            className="rounded-full pointer-events-none border-slate-300 text-slate-600"
                            disabled={uploadingType === doc.type}
                          >
                            {uploadingType === doc.type ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            {uploaded ? "Replace" : "Upload"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Submit Button */}
        {verificationStatus?.merchant_status === "pending_documents" && (
          <div className="mt-8 text-center">
            <Button
              onClick={handleSubmitForReview}
              disabled={!canSubmit || submitting}
              className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 h-12"
              data-testid="submit-for-review-btn"
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Submit for Review
                </>
              )}
            </Button>
            {!allRequiredDocumentsUploaded && (
              <p className="text-sm text-slate-500 mt-2">
                Please upload all required documents before submitting
              </p>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-slate-100 rounded-xl p-6">
          <h4 className="font-semibold text-slate-900 mb-2">Need Help?</h4>
          <p className="text-sm text-slate-600">
            If you have questions about the verification process or required documents, 
            please contact our support team at <strong>support@veyronix.co.uk</strong>
          </p>
        </div>
      </main>
    </div>
  );
};

export default MerchantDocumentsPage;
